import React, { useState, useEffect, useCallback } from 'react';
import TemplateTiles from './TemplateTiles';
import PageHeader from './components/PageHeader';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import CopyActionIcon from './components/CopyActionIcon';
import TrashActionIcon from './components/TrashActionIcon';
import UnarchiveActionIcon from './components/UnarchiveActionIcon';
import { ListViewIcon, TileViewIcon } from './components/ViewToggleIcons';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import NewTemplateChooserModal from './NewTemplateChooserModal';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import { showToastSuccess, showToastError } from './components/ToastNotifications';
import { useRef } from 'react';
import {
  removeTemplate,
  unarchiveTemplate,
} from './hooks/useTemplates';
import useTemplatesPageResults from './hooks/useTemplatesPageResults';
import { useUrlFilters, useUrlParam } from './hooks/urlParams';
import { PAGE_SIZE_OPTIONS } from './constants';
import { formatDate } from './utils/formatDate';
import { getNextSortCriterion } from './utils/tableSort';
import TemplateFormatBadge, { TemplateFormatOption } from './TemplateFormatBadge';

function serializeTemplateActive(value: boolean | null): string {
  return value === null ? 'all' : String(value);
}

function deserializeTemplateActive(raw: string | string[]): boolean | null {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'all') return null;
  return true;
}

export default function TemplatesPage({onAdd, onAddFromTemplate, onEdit, onCopy, onCloneAs = undefined,
    organizationId, currentUser }) {
  const [view, setView] = useUrlParam<string>('view', 'tiles');
  const descendingFirstFields = useRef(new Set(['lastModified']));
    const [filters, setFilters] = useUrlFilters<{
        page: number;
        pageSize: number;
        name: string;
        marketId: number | null;
        brandId: number | null;
        active: boolean | null;
    }>({
        page: { default: 0, type: 'number' },
        pageSize: {
          default: PAGE_SIZE_OPTIONS[0],
          type: 'number',
          validate: (value) => PAGE_SIZE_OPTIONS.includes(
            value as (typeof PAGE_SIZE_OPTIONS)[number],
          ) ? value : undefined,
        },
        name: { default: '', type: 'string' },
        marketId: { default: null, type: 'number' },
        brandId: { default: null, type: 'number' },
        active: {
          default: true,
          type: 'boolean',
          serialize: serializeTemplateActive,
          deserialize: deserializeTemplateActive,
        },
    });
    const pageParam = filters.page;
    const pageSizeParam = filters.pageSize;
    const setPageParam = useCallback((page: number) => {
      setFilters((current) => ({ ...current, page }));
    }, [setFilters]);
    const setPageSizeParam = useCallback((pageSize: number) => {
      setFilters((current) => ({ ...current, pageSize }));
    }, [setFilters]);
    const [sort, setSort] = useState({ fieldName: 'name', isAscending: true });

    const [showChooser, setShowChooser] = useState(false);
    const [archiveTarget, setArchiveTarget] = useState<any>(null);
    const [archiveBusy, setArchiveBusy] = useState(false);
    const [openFormatMenuId, setOpenFormatMenuId] = useState<string | number | null>(null);
    const canCopyTemplates = currentUser?.roleActions?.indexOf('WriteTemplates') >= 0 ||
      currentUser?.roleActions?.indexOf('AdminTemplates') >= 0;
    const [resultsOrganizationId, setResultsOrganizationId] = useState(organizationId);

    if (resultsOrganizationId !== organizationId) {
      setResultsOrganizationId(organizationId);
      setFilters((current) => ({ ...current, page: 0 }));
    }

    const { results, marketOptions, brandOptions, refreshResults } = useTemplatesPageResults({
      organizationId,
      filters,
      sort,
    });

    const changeView = useCallback((nextView: string) => {
      setOpenFormatMenuId(null);
      setView(nextView);
    }, [setView]);

    useEffect(() => {
      setOpenFormatMenuId(null);
    }, [view]);

    const toggleSortDirection = useCallback((fieldName: string) => {
      setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
      setPageParam(0);
    }, [setPageParam]);

    const handleCopy = useCallback(async (t) => {
        if (!onCopy) return;
        const allNames = results.data.map((item) => item.name);
        const copied = await onCopy(t, allNames);
        if (copied === true) {
          refreshResults();
        }
    }, [onCopy, results.data, refreshResults]);

    const handleArchiveConfirm = useCallback(async () => {
        if (!archiveTarget) return;
        setArchiveBusy(true);
        try {
            await removeTemplate(archiveTarget.id);
            showToastSuccess(`${archiveTarget.name} archived successfully.`);
            refreshResults();
        } catch {
            showToastError('The template could not be archived. Please try again.');
        } finally {
            setArchiveBusy(false);
            setArchiveTarget(null);
        }
    }, [archiveTarget, refreshResults]);

    const handleUnarchive = useCallback(async (template) => {
        try {
            await unarchiveTemplate(Number(template.id));
            showToastSuccess(`${template.name} restored successfully.`);
            refreshResults();
        } catch {
            showToastError('Failed to restore template.');
        }
    }, [refreshResults]);

  return (
    <div className="stack" data-testid="page-templates">
      <PageHeader
        title="Templates"
        subtitle="Print Templates"
      />

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
            placeholder="Search templates or markets"
            value={filters.name}
            onChange={(e) => {
                setPageParam(0);
                setFilters((prev) => ({ ...prev, name: e.target.value }));
            }}
              data-testid="templates-search"
            />
            <select
              className="select"
              value={filters.marketId ?? 'all'}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextMarketId = raw === 'all' ? null : Number(raw);
                  setPageParam(0);
                  setFilters((f) => ({ ...f, marketId: Number.isFinite(nextMarketId) ? nextMarketId : null }));
              }}
              data-testid="templates-filter-market"
            >
              <option value="all">All Markets</option>
              {(marketOptions || [])
                .slice()
                .sort((a, b) => (a.name || a.code || '').localeCompare((b.name || b.code || ''), undefined, { sensitivity: 'base' }))
                .map((m) => (
                  <option key={String(m.id)} value={String(m.id)}>{m.name || m.code}</option>
              ))}
            </select>
            <select
              className="select"
              aria-label="Filter templates by brand"
              value={filters.brandId ?? 'all'}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextBrandId = raw === 'all' ? null : Number(raw);
                  setPageParam(0);
                  setFilters((f) => ({ ...f, brandId: Number.isFinite(nextBrandId) ? nextBrandId : null }));
              }}
              data-testid="templates-filter-brand"
            >
              <option value="all">All Brands</option>
              {(brandOptions || []).map((brand) => (
                <option key={String(brand.id)} value={String(brand.id)}>{brand.name}</option>
              ))}
            </select>
            <select
              className="select"
              value={filters.active === null ? 'all' : String(filters.active)}
              onChange={(e) => {
                  const raw = String(e.target.value || 'all');
                  const nextActive = raw === 'all' ? null : (raw === 'true');
                  setPageParam(0);
                  setFilters((prev) => ({ ...prev, active: nextActive }));
              }}
              data-testid="templates-filter-status"
            >
              <option value="true">Active</option>
              <option value="false">Archived</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="filter-right">
            <div className="segmented view-toggle">
              <button
                type="button"
                className={`segment view-toggle-btn ${view === 'tiles' ? 'active' : ''}`}
                onClick={() => changeView('tiles')}
                aria-label="Tile view"
                title="Tile view"
              >
                <TileViewIcon />
              </button>
              <button
                type="button"
                className={`segment view-toggle-btn ${view === 'list' ? 'active' : ''}`}
                onClick={() => changeView('list')}
                aria-label="List view"
                title="List view"
              >
                <ListViewIcon />
              </button>
            </div>
            {(currentUser?.roleActions?.indexOf('WriteTemplates') >= 0 ||
              currentUser?.roleActions?.indexOf('AdminTemplates') >= 0) && <button className="button cta" onClick={() => setShowChooser(true)} data-testid="templates-add">
              <span className="plus" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              New Template
            </button>}
          </div>
        </div>
      </div>

      {view === 'list' ? (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <SortableTableHeader label="Name" fieldName="name" sort={sort} onSort={toggleSortDirection} />
                <SortableTableHeader label="Market" fieldName="market" sort={sort} onSort={toggleSortDirection} />
                <SortableTableHeader label="Format" fieldName="format" sort={sort} onSort={toggleSortDirection} />
                <SortableTableHeader label="Last Updated" fieldName="lastModified" sort={sort} onSort={toggleSortDirection} />
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {results.data.filter(_ => !_.isPublic).map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="template-market-column">
                    <div className="template-market-tags">
                      {Array.isArray(t.markets) && t.markets.length > 0 ? (
                        t.markets.map((marketName, index) => (
                          <span key={`${t.id}-${marketName}-${index}`} className="tag" style={{ margin: '4px' }}>
                            {marketName}
                          </span>
                        ))
                      ) : (
                        t.market || ''
                      )}
                    </div>
                  </td>
                   <td className="template-format-column">
                     <TemplateFormatBadge
                       template={t}
                       interactive={canCopyTemplates}
                       isOpen={openFormatMenuId === t.id}
                       alignment="left"
                       onOpenChange={(open) => setOpenFormatMenuId(open ? t.id : null)}
                       onCloneAs={(target: TemplateFormatOption) => onCloneAs?.(t, target)}
                     />
                   </td>
                  <td className="template-updated-column">{formatDate(t?.lastModified)}</td>
                  <td className="col-actions">
                    <div className="table-actions">
                        {(currentUser?.roleActions?.indexOf('WriteTemplates') >= 0 ||
                          currentUser?.roleActions?.indexOf('AdminTemplates') >= 0) && <IconButton title="Edit" ariaLabel={`Edit ${t.name}`} onClick={() => onEdit && onEdit(t)} testId={`template-edit-${t.id}`}>
                          <EditActionIcon />
                        </IconButton>}
                        {(currentUser?.roleActions?.indexOf('WriteTemplates') >= 0 ||
                          currentUser?.roleActions?.indexOf('AdminTemplates') >= 0) && <IconButton title="Copy" ariaLabel={`Copy ${t.name}`} onClick={() => handleCopy(t)} testId={`template-copy-${t.id}`}>
                          <CopyActionIcon />
                        </IconButton>}
	                        {(currentUser?.roleActions?.indexOf('AdminTemplates') >= 0) && filters.active !== false && <IconButton title="Archive" ariaLabel={`Archive ${t.name}`} onClick={() => setArchiveTarget(t)} testId={`template-archive-${t.id}`}>
	                          <TrashActionIcon />
	                        </IconButton>}
	                        {(currentUser?.roleActions?.indexOf('AdminTemplates') >= 0) && filters.active === false && <IconButton title="Unarchive" ariaLabel={`Unarchive ${t.name}`} onClick={() => handleUnarchive(t)} testId={`template-unarchive-${t.id}`}>
	                          <UnarchiveActionIcon />
	                        </IconButton>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          ) : (
               <TemplateTiles templates={results.data.filter(_ => !_.isPublic)} currentUser={currentUser} onEdit={onEdit} onCopy={handleCopy} onCloneAs={onCloneAs} openFormatMenuId={openFormatMenuId} onOpenFormatMenuChange={setOpenFormatMenuId} onArchive={(t) => setArchiveTarget(t)} onUnarchive={handleUnarchive} showUnarchive={filters.active === false} />
          )}
          <Pagination
              page={pageParam}
              totalPages={Math.ceil(results.total / pageSizeParam)}
              onPrev={() => setPageParam(pageParam - 1)}
              onNext={() => setPageParam(pageParam + 1)}
              format="of"
              prevLabel="◀"
              nextLabel="▶"
              pageSize={pageSizeParam}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              testId="templates-pagination"
              onPageSizeChange={(size) => {
                setPageSizeParam(size);
                setPageParam(0);
              }}
           />
      {showChooser && (
        <NewTemplateChooserModal
          key={`${organizationId}-${currentUser?.id ?? ''}`}
          organizationId={organizationId}
          userId={currentUser?.id}
          onSelectBlank={(formatSelection) => {
            setShowChooser(false);
            if (onAdd) onAdd(formatSelection);
          }}
          onSelectTemplate={(template, cloneName, formatSelection, marketIds, provenance) => {
            setShowChooser(false);
            if (onAddFromTemplate) onAddFromTemplate(template, cloneName, formatSelection, marketIds, provenance);
          }}
          onClose={() => setShowChooser(false)}
        />
      )}
      <FullScreenConfirmOverlay
	        show={!!archiveTarget}
	        title="Archive Template"
	        message={`Are you sure you would like to archive ${archiveTarget?.name}?`}
	        onCancel={() => setArchiveTarget(null)}
	        onConfirm={handleArchiveConfirm}
	        confirmLabel="Archive"
	        busy={archiveBusy}
	        testId="template-archive-confirm"
	      />
    </div>
  );
}
